'use client';

import { useState } from 'react';
import {
  SpeechAssessmentResponse,
  WordAssessment
} from '@/src/types/assessment';

interface ScoreBoxProps {
  label: string;
  score: number;
}

const ScoreBox = ({ label, score }: ScoreBoxProps) => {
  const getColor = (score: number) => {
    if (score >= 80) return 'bg-green-100 text-green-800';
    if (score >= 60) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  return (
    <div className={`rounded-lg p-3 ${getColor(score)}`}>
      <div className='text-base font-medium'>{label}</div>
      <div className='text-lg font-semibold'>{score?.toFixed(1)}%</div>
    </div>
  );
};

interface AssessmentResultsProps {
  assessment: SpeechAssessmentResponse | null;
}

export const AssessmentResults = ({ assessment }: AssessmentResultsProps) => {
  const [selectedWord, setSelectedWord] = useState<string | null>(null);

  if (!assessment) return null;

  const getWordColor = (score: number) => {
    return score < 60 ? 'text-red-600' : 'text-gray-900';
  };

  const findWordAssessment = (word: string): WordAssessment | undefined => {
    return assessment.wordAssessments?.find((wa) => wa.word === word);
  };

  return (
    <div className='space-y-4'>
      {/* Overall Assessment */}
      <div className='grid grid-cols-2 gap-4 md:grid-cols-4'>
        <ScoreBox
          label='Accuracy'
          score={assessment.overallAssessment.accuracy ?? 0}
        />
        {assessment.overallAssessment.fluency !== undefined && (
          <ScoreBox
            label='Fluency'
            score={assessment.overallAssessment.fluency}
          />
        )}
        {assessment.overallAssessment.completeness !== undefined && (
          <ScoreBox
            label='Completeness'
            score={assessment.overallAssessment.completeness}
          />
        )}
        {assessment.overallAssessment.pronunciation !== undefined && (
          <ScoreBox
            label='Pronunciation'
            score={assessment.overallAssessment.pronunciation}
          />
        )}
      </div>

      {/* Word Assessments */}
      {assessment.wordAssessments && assessment.wordAssessments.length > 0 && (
        <div className='mt-6'>
          <h3 className='mb-3 text-lg font-medium'>Word Assessments</h3>
          <div className='space-y-3'>
            {assessment.wordAssessments.map((wa) => (
              <div key={wa.word} className='space-y-2'>
                <button
                  onClick={() =>
                    setSelectedWord(selectedWord === wa.word ? null : wa.word)
                  }
                  className={`w-full text-left ${getWordColor(wa.assessment?.accuracy ?? 0)}`}
                >
                  <span className='font-medium'>{wa.word}</span>
                  <span className='ml-2 text-base'>
                    ({(wa.assessment?.accuracy ?? 0).toFixed(1)}%)
                  </span>
                </button>

                {/* Phoneme Details */}
                {selectedWord === wa.word && wa.phonemeAssessments && (
                  <div className='ml-4 rounded-lg bg-gray-50 p-3'>
                    <h4 className='mb-2 text-base font-medium'>
                      Phoneme Breakdown
                    </h4>
                    <div className='grid grid-cols-2 gap-2 md:grid-cols-3'>
                      {wa.phonemeAssessments.map((pa) => (
                        <div
                          key={pa.phoneme}
                          className={`rounded p-2 ${
                            pa.assessment.accuracy < 60
                              ? 'bg-red-100'
                              : 'bg-gray-100'
                          }`}
                        >
                          <div className='text-base font-medium'>
                            {pa.phoneme}
                          </div>
                          <div className='text-base'>
                            {(pa.assessment?.accuracy ?? 0).toFixed(1)}%
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
